import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Order } from '@Types/cart';
import ChatBubblesIcon from 'components/icons/chatbubbles';
import ChevronRightIcon from 'components/icons/chevron-right';
import LogOutIcon from 'components/icons/log-out';
import UserIcon from 'components/icons/user';
import OrderItem from 'components/revelo-ui/account/details/order-history/order/orderItem';
import { useFormat } from 'helpers/hooks/useFormat';
import useHash from 'helpers/hooks/useHash';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { Reference, ReferenceLink } from 'helpers/reference';
import { disableScrollOnBody } from 'helpers/utils/disableScrollOnBody';
import { tablet, desktop } from 'helpers/utils/screensizes';
import { useAccount, useCart } from 'frontastic';
import {
  PersonalDataSection,
  GeneralSection,
  SecuritySection,
  OrdersHistorySection,
  NotificationsSection,
  OrderDetailSection,
} from './sections/exporter';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export interface LinkItem {
  linkText?: string;
  link?: Reference;
}

export interface BikeAlarmButton {
  noBikeAlarmText?: string;
  noBikeAlarmLink?: Reference;
}

export interface AccountDetailsProps {
  loginLink?: Reference;
  enableBtn?: boolean;
  buttonText?: string;
  buttonLink?: Reference;
  linkList?: LinkItem[];
  noBikeAlarmMarkdown?: string;
  noBikeAlarmButtons?: BikeAlarmButton[];
  unsubscribeAllMarkdown?: string;
  newsletterText?: string;
  newsletterDisclaimerText?: string;
  listId?: string;
  permissionId?: string;
  newsletterSuccessMessage?: string;
}

const AccountDetails: React.FC<AccountDetailsProps> = (data) => {
  const [showTabContent, setShowTabContent] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | undefined>();
  const [lastOrder, setLastOrder] = useState<Order | undefined>();

  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const { orderHistory, getOrder } = useCart();
  const { account, logout } = useAccount();
  const [hash, ...hashParams] = useHash().split('-');
  const { events: routerEvents } = useRouter();

  const [isTabletSize] = useMediaQuery(tablet);
  const [isDesktopSize] = useMediaQuery(desktop);

  //tabs
  const tabs = [
    { name: formatAccountMessage({ id: 'general', defaultMessage: 'General' }), href: '#' },
    { name: formatAccountMessage({ id: 'my.orders.history', defaultMessage: 'Order history' }), href: '#orders' },
    { name: formatAccountMessage({ id: 'personal.data', defaultMessage: 'Personal Data' }), href: '#personalData' },
    { name: formatAccountMessage({ id: 'notifications', defaultMessage: 'Notifications' }), href: '#notifications' },
    { name: formatAccountMessage({ id: 'change.password', defaultMessage: 'Change Password' }), href: '#security' },
  ];

  const handleTabChange = () => {
    if (!isDesktopSize) {
      disableScrollOnBody(true);
      window.scrollTo({ top: 0, left: 0 });
    }
    setShowTabContent(true);
  };

  const onSelectOrder = (order: Order) => {
    setSelectedOrder(order);
    handleTabChange();
  };

  const closeTab = () => {
    if (!isDesktopSize) {
      disableScrollOnBody(false);
    }
    setShowTabContent(false);
  };

  //tabs-content mapping
  const mapping = {
    '#': <GeneralSection {...data} order={lastOrder} />,
    '#orders': <OrdersHistorySection {...data} onSelectOrder={onSelectOrder} onCloseContent={closeTab} />,
    '#personalData': <PersonalDataSection onCloseContent={closeTab} />,
    '#notifications': <NotificationsSection {...data} onCloseContent={closeTab} />,
    '#security': <SecuritySection onCloseContent={closeTab} />,
    '#orderDetail': <OrderDetailSection {...data} order={selectedOrder} onCloseContent={closeTab} />,
  };

  //current rendered content
  const Content = mapping[hash] || mapping['#'];

  useEffect(() => {
    disableScrollOnBody(isDesktopSize ? false : showTabContent);
  }, [isDesktopSize, showTabContent]);

  useEffect(() => {
    routerEvents.on('routeChangeStart', () => disableScrollOnBody(false));
  }, [routerEvents]);

  useEffect(() => {
    if (orderHistory && !lastOrder) {
      orderHistory(1, 0).then((data) => {
        setLastOrder(data[0]);
      });
    }
  }, [lastOrder, orderHistory]);

  useEffect(() => {
    if (hash === '#orderDetail' && hashParams[0] && !selectedOrder) {
      getOrder(hashParams[0]).then((order) => {
        setSelectedOrder(order);
      });
    }
  }, []);

  return (
    <div>
      {/* Sidebar area */}
      <div className="flex gap-10 py-6 lg:py-12">
        <div className="basis-full lg:basis-1/4">
          <div className="mb-6 flex flex-col gap-4">
            <div className="font-headline text-6xl lg:text-2xl">
              {!isTabletSize && account.firstName ? `Willkommen zurück, ${account.firstName}!` : 'Mein Kundenkonto'}
            </div>
            <div className="flex gap-2">
              <UserIcon className="size-6" />
              <div>{account.email}</div>
            </div>
          </div>
          {!isTabletSize && lastOrder && (
            <div className="mb-8 mt-2">
              <div className="mb-2 font-headline text-xl">
                {formatAccountMessage({ id: 'my.last.order', defaultMessage: 'Meine letzte Bestellung' })}
              </div>
              <OrderItem order={lastOrder} link="#orderDetail" onClick={onSelectOrder} />
            </div>
          )}
          <nav className="flex flex-col gap-2.5">
            {tabs
              .filter((tab) => tab.href !== '#')
              .map((tab) => (
                <a
                  key={tab.name}
                  href={tab.href}
                  className={classNames(
                    'flex justify-between px-4 py-5 text-neutral-950 shadow-sm',
                    tab.href === hash ? 'bg-neutral-200' : 'bg-neutral-50',
                  )}
                  onClick={handleTabChange}
                >
                  <span>{tab.name}</span>
                  <ChevronRightIcon className="size-6" />
                </a>
              ))}

            <div className="mt-6 ">
              {!isDesktopSize && data.enableBtn && (
                <ReferenceLink target={data.buttonLink} className="mb-4 flex gap-2 text-secondary-600">
                  <ChatBubblesIcon className="size-6 fill-secondary-600" />
                  <span className="border-b border-secondary-600 text-md">{data.buttonText}</span>
                </ReferenceLink>
              )}

              <button className="flex gap-2 text-secondary-600" onClick={logout}>
                <LogOutIcon className="size-6 fill-secondary-600" />
                <span className="border-b border-secondary-600 text-md">Ausloggen</span>
              </button>
            </div>
          </nav>
        </div>
        <div
          className={classNames(
            'fixed left-0 top-20 z-10 mb-20 flex h-full w-full basis-3/4 flex-col gap-8 overflow-y-auto bg-white p-4 transition-transform lg:relative lg:top-0 lg:transform-none lg:p-0',
            showTabContent ? 'transform-none' : 'translate-x-full',
          )}
          style={{ height: `calc(100% - 80px)` }}
        >
          {Content}
        </div>
      </div>
    </div>
  );
};

export default AccountDetails;
